import { useState, type Dispatch, type SetStateAction } from 'react';
import {
  useFacilitySearch, useResidenceTypes, useFacilityCandidates, useCreateFacility,
  AddressTaken, homeName,
  type FacilityOption, type FacilityCandidate, type ResidenceTypeOption, type NewHomeInput, type NewHouseholdInput,
} from '../api/places.ts';
import { ApiError } from '../api/client.ts';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const secondary =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';
const primary =
  'min-h-[48px] rounded-lg bg-neutral-900 px-4 font-semibold text-white ' +
  'hover:bg-neutral-800 active:bg-neutral-700 disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';

/** The type the building must be, when the form has already chosen a kind of placement. */
export interface LockedType {
  code: string;
  /** The kind of placement chosen, as the reader chose it: "Rehab / Sub-Acute". */
  kindLabel: string;
}

/**
 * Where a client is going, when it is a building.
 *
 * **Nobody sets out to add a facility.** They hit the need while placing
 * someone somewhere new: intake, a move, a stay. So the search is here, and
 * when it finds nothing, "Add a new facility" is right beneath it, and adding
 * one lands back in the same form with it chosen (Arthur, 23 September).
 *
 * The search matches name, another spelling and street, because a court order
 * may name the building differently from the catalogue. `lockedType` narrows it
 * to one facility type, the rule `move_client()` enforces, once a form has
 * chosen a kind of placement.
 */
export function FacilityPicker({
  id, selected, onSelect, lockedType = null, searchLabel = 'Find a building',
}: {
  id: string;
  selected: FacilityOption | null;
  onSelect: (f: FacilityOption | null) => void;
  lockedType?: LockedType | null;
  searchLabel?: string;
}) {
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const search = useFacilitySearch(q.trim(), lockedType?.code ?? null);
  const types = useResidenceTypes('facility');
  const mayAdd = types.data?.may_add ?? false;

  if (selected) {
    return (
      <div className="mt-1 rounded-lg border border-neutral-300 bg-neutral-50 px-3 py-2">
        <p className="font-semibold text-neutral-900">{selected.facility_name}</p>
        <p className="text-sm text-neutral-700">
          {[selected.street_address, selected.city].filter(Boolean).join(', ')}
          {selected.residence_type_label ? ` · ${selected.residence_type_label}` : ''}
        </p>
        <button type="button" onClick={() => onSelect(null)} className={`${secondary} mt-2`}>
          Choose a different building
        </button>
      </div>
    );
  }

  if (adding) {
    return (
      <NewFacilityForm
        initialName={q.trim()}
        lockedType={lockedType}
        types={types.data?.residence_types ?? []}
        onDone={(f) => { setAdding(false); onSelect(f); }}
        onCancel={() => setAdding(false)}
      />
    );
  }

  const rows = search.data?.facilities ?? [];
  const searched = q.trim().length > 0;
  const nothing = searched && search.isSuccess && rows.length === 0;

  return (
    <div>
      <label className={label} htmlFor={id}>{searchLabel}</label>
      <input
        id={id} type="search" value={q} className={field} autoComplete="off"
        onChange={(e) => setQ(e.target.value)}
        placeholder="Name or street of a nursing home, hospital…"
      />
      {lockedType && (
        <p className={hint}>
          Only buildings recorded as{' '}
          {types.data?.residence_types.find((t) => t.code === lockedType.code)?.label ?? lockedType.code}{' '}
          are listed, because that is what {lockedType.kindLabel} means.
        </p>
      )}

      {search.isError && (
        <p className="mt-2 text-sm text-rose-800">The buildings could not be searched. Try again.</p>
      )}

      {rows.length > 0 && (
        <ul className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-neutral-200">
          {rows.map((f) => (
            <li key={f.id} className="border-b border-neutral-100 last:border-b-0">
              <button
                type="button" onClick={() => onSelect(f)}
                className="block min-h-[44px] w-full px-3 py-2 text-left hover:bg-neutral-50"
              >
                <span className="font-medium text-neutral-900">{f.facility_name}</span>
                <span className="block text-sm text-neutral-600">
                  {[f.street_address, f.city].filter(Boolean).join(', ')}
                  {f.residence_type_label ? ` · ${f.residence_type_label}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Only a successful answer to the text in the box says "nothing". */}
      {nothing && (
        <p className="mt-2 text-neutral-700">No building matches “{q.trim()}”.</p>
      )}

      {mayAdd && (
        <div className="mt-2">
          {nothing ? (
            <button type="button" onClick={() => setAdding(true)} className={primary}>
              Add a new facility
            </button>
          ) : (
            <button type="button" onClick={() => setAdding(true)} className={secondary}>
              Not in the list? Add a new facility
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A building nobody has recorded.
 *
 * **Its type is required, and the form says why before it is chosen** (Arthur,
 * 23 September): it sets how often every client placed there must be visited,
 * so there is no "unknown" and no default. Each type carries a plain
 * description and the rate it sets today.
 *
 * **The duplicate guard offers, never blocks.** Before saving, the address is
 * checked. A building already there is offered with how many of our clients
 * live in it; the person uses it, or says this is a different building, and
 * that answer is recorded.
 */
function NewFacilityForm({
  initialName, lockedType, types, onDone, onCancel,
}: {
  initialName: string;
  lockedType: LockedType | null;
  types: ResidenceTypeOption[];
  onDone: (f: FacilityOption) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [type, setType] = useState(lockedType?.code ?? '');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('New York');
  const [zip, setZip] = useState('');
  const [county, setCounty] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [candidates, setCandidates] = useState<FacilityCandidate[] | null>(null);
  const [raced, setRaced] = useState(false);

  const check = useFacilityCandidates();
  const create = useCreateFacility();
  const chosenType = types.find((t) => t.code === type) ?? null;

  const ready =
    name.trim() !== '' && type !== '' && street.trim() !== '' && city.trim() !== '' &&
    state.trim().length >= 2 && (type !== 'Other' || notes.trim() !== '');

  const body = (distinct: string[]) => ({
    facility_name: name.trim(),
    facility_type: type,
    street_address: street.trim(),
    city: city.trim(),
    state: state.trim(),
    ...(zip.trim() ? { zip: zip.trim() } : {}),
    ...(county.trim() ? { county: county.trim() } : {}),
    ...(phone.trim() ? { phone: phone.trim() } : {}),
    ...(notes.trim() ? { notes: notes.trim() } : {}),
    distinct_from: distinct,
  });

  const askAddress = async () => {
    const found = await check.mutateAsync({
      street_address: street.trim(), city: city.trim(), name: name.trim(),
      ...(zip.trim() ? { zip: zip.trim() } : {}),
    });
    return found.candidates;
  };

  const save = async (distinct: string[]) => {
    try {
      const made = await create.mutateAsync(body(distinct));
      onDone(made.facility);
    } catch (err) {
      if (err instanceof AddressTaken) {
        // Someone added a building here since we asked. Ask again and offer it.
        setRaced(true);
        setCandidates(await askAddress());
      }
    }
  };

  const submit = async () => {
    if (!ready || check.isPending || create.isPending) return;
    const found = await askAddress();
    if (found.length > 0) {
      setCandidates(found);
      return;
    }
    await save([]);
  };

  if (candidates && candidates.length > 0) {
    return (
      <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
        <p className="font-semibold text-neutral-900">
          {raced
            ? 'Someone has just added a building at this address.'
            : 'There is already a building at this address.'}{' '}
          Is it one of these?
        </p>
        <ul className="mt-2 space-y-2">
          {candidates.map((c) => {
            const wrongType = lockedType !== null && c.facility_type !== lockedType.code;
            return (
              <li key={c.id} className="rounded-lg border border-neutral-200 bg-white p-3">
                <p className="font-medium text-neutral-900">{c.facility_name}</p>
                <p className="text-sm text-neutral-700">
                  {[c.street_address, c.city, c.zip].filter(Boolean).join(', ')}
                  {c.residence_type_label ? ` · ${c.residence_type_label}` : ' · type not recorded'}
                </p>
                <p className="text-sm text-neutral-700">
                  {c.clients_here === 0
                    ? 'None of our clients lives here now.'
                    : `${c.clients_here} of our ${c.clients_here === 1 ? 'clients lives' : 'clients live'} here now.`}
                </p>
                {wrongType ? (
                  <p className="mt-1 text-sm text-neutral-700">
                    It is recorded as {c.residence_type_label ?? 'having no type'}, not{' '}
                    {lockedType.kindLabel}. To place someone here, choose that kind of placement.
                  </p>
                ) : (
                  <button
                    type="button" className={`${secondary} mt-2`}
                    onClick={() => onDone({
                      id: c.id, facility_name: c.facility_name, facility_type: c.facility_type,
                      street_address: c.street_address, city: c.city, state: c.state, county: null,
                      residence_type_label: c.residence_type_label,
                      placement_kind_label: c.placement_kind_label,
                    })}
                  >
                    Use this one
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button" className={primary} disabled={create.isPending}
            onClick={() => void save(candidates.map((c) => c.id))}
          >
            {create.isPending ? 'Adding…' : 'None of these: it is a different building'}
          </button>
          <button type="button" className={secondary} onClick={() => { setCandidates(null); setRaced(false); }}>
            Back to the details
          </button>
        </div>
        <Failure error={create.error instanceof AddressTaken ? null : create.error} />
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3">
      <p className="font-semibold text-neutral-900">A new facility</p>

      <div>
        <label className={label} htmlFor="nf-name">Name</label>
        <input id="nf-name" className={field} value={name} maxLength={200}
               onChange={(e) => setName(e.target.value)} />
      </div>

      <fieldset>
        <legend className={label}>What kind of place it is</legend>
        <p className={hint}>
          <strong>This sets how often clients here must be visited</strong>, for
          everyone placed here from now on. If you are not sure, look it up: it
          cannot be left unknown.
        </p>
        {lockedType ? (
          <p className="mt-2 rounded-lg bg-neutral-50 px-3 py-2 text-neutral-900">
            {chosenType?.label ?? lockedType.code}, from the kind of placement you chose
            ({lockedType.kindLabel}).
            {chosenType?.visits_per_year != null &&
              ` Clients here must be visited ${chosenType.visits_per_year} times a year under current policy.`}
          </p>
        ) : (
          <div className="mt-2 space-y-1">
            {types.map((t) => (
              <label
                key={t.code}
                className={`flex min-h-[44px] cursor-pointer gap-3 rounded-lg border px-3 py-2 ${
                  type === t.code ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200'
                }`}
              >
                <input
                  type="radio" name="nf-type" value={t.code} checked={type === t.code}
                  onChange={() => setType(t.code)} className="mt-1 size-5 shrink-0"
                />
                <span>
                  <span className="font-medium text-neutral-900">{t.label}</span>
                  {t.description && <span className="block text-sm text-neutral-700">{t.description}</span>}
                  {t.visits_per_year != null && (
                    <span className="block text-sm text-neutral-600">
                      {t.visits_per_year} visits a year under current policy.
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        )}
      </fieldset>

      <AddressFields
        prefix="nf" street={street} setStreet={setStreet} city={city} setCity={setCity}
        state={state} setState={setState} zip={zip} setZip={setZip}
        county={county} setCounty={setCounty}
      />

      <div>
        <label className={label} htmlFor="nf-phone">
          Phone <span className="font-normal text-neutral-500">— if known</span>
        </label>
        <input id="nf-phone" className={field} value={phone} maxLength={32}
               onChange={(e) => setPhone(e.target.value)} />
      </div>

      <div>
        <label className={label} htmlFor="nf-notes">
          Notes{' '}
          <span className="font-normal text-neutral-500">
            {type === 'Other' ? '— say what kind of place it is' : '— optional'}
          </span>
        </label>
        <textarea id="nf-notes" className={`${field} min-h-[88px] py-2`} value={notes} maxLength={4000}
                  onChange={(e) => setNotes(e.target.value)} />
      </div>

      <Failure error={check.error ?? (create.error instanceof AddressTaken ? null : create.error)} />

      <div className="flex flex-wrap gap-2">
        <button type="button" className={primary} disabled={!ready || check.isPending || create.isPending}
                onClick={() => void submit()}>
          {check.isPending ? 'Checking the address…' : create.isPending ? 'Adding…' : 'Add this facility'}
        </button>
        <button type="button" className={secondary} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export function AddressFields({
  prefix, street, setStreet, city, setCity, state, setState, zip, setZip, county, setCounty,
  unit, setUnit,
}: {
  prefix: string;
  street: string; setStreet: (v: string) => void;
  city: string; setCity: (v: string) => void;
  state: string; setState: (v: string) => void;
  zip: string; setZip: (v: string) => void;
  county: string; setCounty: (v: string) => void;
  unit?: string; setUnit?: (v: string) => void;
}) {
  return (
    <>
      <div>
        <label className={label} htmlFor={`${prefix}-street`}>Street address</label>
        <input id={`${prefix}-street`} className={field} value={street} maxLength={128}
               onChange={(e) => setStreet(e.target.value)} autoComplete="off" />
      </div>
      {setUnit && (
        <div>
          <label className={label} htmlFor={`${prefix}-unit`}>
            Apartment or unit <span className="font-normal text-neutral-500">— if any</span>
          </label>
          <input id={`${prefix}-unit`} className={field} value={unit ?? ''} maxLength={32}
                 onChange={(e) => setUnit(e.target.value)} autoComplete="off" />
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={label} htmlFor={`${prefix}-city`}>City, borough or town</label>
          <input id={`${prefix}-city`} className={field} value={city} maxLength={64}
                 onChange={(e) => setCity(e.target.value)} autoComplete="off" />
        </div>
        <div>
          <label className={label} htmlFor={`${prefix}-state`}>State</label>
          <input id={`${prefix}-state`} className={field} value={state} maxLength={32}
                 onChange={(e) => setState(e.target.value)} />
        </div>
        <div>
          <label className={label} htmlFor={`${prefix}-zip`}>ZIP</label>
          <input id={`${prefix}-zip`} className={field} value={zip} maxLength={10} inputMode="numeric"
                 onChange={(e) => setZip(e.target.value)} autoComplete="off" />
        </div>
        <div>
          <label className={label} htmlFor={`${prefix}-county`}>
            County <span className="font-normal text-neutral-500">— as the address gives it</span>
          </label>
          <input id={`${prefix}-county`} className={field} value={county} maxLength={64}
                 onChange={(e) => setCounty(e.target.value)} autoComplete="off" />
        </div>
      </div>
    </>
  );
}

/** A home being recorded, as the form holds it before it is sent. */
export interface HomeDraft {
  property_type: string;
  tenure: '' | NewHomeInput['tenure'];
  street_address: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  neighbourhood: string;
  notes: string;
}

export const EMPTY_HOME: HomeDraft = {
  property_type: '', tenure: '', street_address: '', unit: '', city: '',
  state: 'New York', zip: '', county: '', neighbourhood: '', notes: '',
};

/** The draft as the API takes it, or null while something required is missing. */
export function homeInput(d: HomeDraft): NewHomeInput | null {
  if (!d.property_type || !d.tenure || !d.street_address.trim() || !d.city.trim() ||
      d.state.trim().length < 2 || (d.property_type === 'Other' && !d.notes.trim())) {
    return null;
  }
  return {
    property_type: d.property_type,
    tenure: d.tenure,
    street_address: d.street_address.trim(),
    city: d.city.trim(),
    state: d.state.trim(),
    ...(d.unit.trim() ? { unit: d.unit.trim() } : {}),
    ...(d.zip.trim() ? { zip: d.zip.trim() } : {}),
    ...(d.county.trim() ? { county: d.county.trim() } : {}),
    ...(d.neighbourhood.trim() ? { neighbourhood: d.neighbourhood.trim() } : {}),
    ...(d.notes.trim() ? { notes: d.notes.trim() } : {}),
  };
}

/**
 * The neighbourhood, optional (091). A person who knows it says so here, and
 * theirs always wins: the address lookup fills one only where nobody did, and
 * never replaces the town that was typed.
 */
export function NeighbourhoodField({ id, value, onChange }: { id: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className={label} htmlFor={id}>
        Neighbourhood <span className="font-normal text-neutral-500">— optional</span>
      </label>
      <input id={id} className={field} value={value} maxLength={96} placeholder="Williamsburg"
             onChange={(e) => onChange(e.target.value)} />
      <p className={hint}>Left blank, the address lookup supplies one where it can, and the page says it did.</p>
    </div>
  );
}

/**
 * A client's home in the community: an apartment they rent, a house they own.
 *
 * **It has no name field.** The name is made from the client's name, the town
 * and the kind of home ("Marisela Okafor's Astoria Apartment"), on every read,
 * so it follows a change of name. The form shows what it will be.
 *
 * The address is the client's, so it is PHI and stays behind the client like
 * everything else about them. It is geocoded like any other place.
 */
export function HomeFields({
  draft, onChange, ownerName,
}: {
  draft: HomeDraft;
  /**
   * A state setter, so each field updates the LATEST draft. Copying the draft
   * from the last render lost fields whenever several changed before a
   * re-render, which is what browser autofill does: found on scratch, where a
   * type and a tenure set together kept only the type.
   */
  onChange: Dispatch<SetStateAction<HomeDraft>>;
  ownerName: string | null;
}) {
  const types = useResidenceTypes('home');
  const set = <K extends keyof HomeDraft>(k: K) => (v: HomeDraft[K]) => onChange((d) => ({ ...d, [k]: v }));
  const chosen = types.data?.residence_types.find((t) => t.code === draft.property_type) ?? null;

  return (
    <div className="space-y-3 rounded-lg border border-neutral-300 p-3">
      <fieldset>
        <legend className={label}>What kind of home</legend>
        <p className={hint}>This sets how often they must be visited while they live here.</p>
        <div className="mt-2 grid gap-1 sm:grid-cols-2">
          {(types.data?.residence_types ?? []).map((t) => (
            <label
              key={t.code}
              className={`flex min-h-[44px] cursor-pointer gap-3 rounded-lg border px-3 py-2 ${
                draft.property_type === t.code ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200'
              }`}
            >
              <input type="radio" name="home-type" value={t.code} checked={draft.property_type === t.code}
                     onChange={() => set('property_type')(t.code)} className="mt-1 size-5 shrink-0" />
              <span>
                <span className="font-medium text-neutral-900">{t.label}</span>
                {t.description && <span className="block text-sm text-neutral-700">{t.description}</span>}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend className={label}>Do they rent it or own it?</legend>
        <div className="mt-1 flex flex-wrap gap-2">
          {([['Rented', 'Rent it'], ['Owned', 'Own it'], ['Unknown', 'Not known yet']] as const).map(([v, text]) => (
            <button key={v} type="button" onClick={() => set('tenure')(v)}
                    className={`${secondary} ${draft.tenure === v ? '!bg-neutral-900 !text-white' : ''}`}>
              {text}
            </button>
          ))}
        </div>
      </fieldset>

      <AddressFields
        prefix="home"
        street={draft.street_address} setStreet={set('street_address')}
        unit={draft.unit} setUnit={set('unit')}
        city={draft.city} setCity={set('city')}
        state={draft.state} setState={set('state')}
        zip={draft.zip} setZip={set('zip')}
        county={draft.county} setCounty={set('county')}
      />

      <NeighbourhoodField id="home-neighbourhood" value={draft.neighbourhood} onChange={set('neighbourhood')} />

      <div>
        <label className={label} htmlFor="home-notes">
          Notes{' '}
          <span className="font-normal text-neutral-500">
            {draft.property_type === 'Other' ? '— say what kind of home it is' : '— optional'}
          </span>
        </label>
        <textarea id="home-notes" className={`${field} min-h-[88px] py-2`} value={draft.notes} maxLength={4000}
                  onChange={(e) => set('notes')(e.target.value)} />
      </div>

      {chosen && draft.city.trim() && (
        <p className="rounded-lg bg-neutral-50 px-3 py-2 text-neutral-800">
          It will be recorded as{' '}
          <strong>{homeName(ownerName, draft.city, chosen.residence_noun)}</strong>. The name is
          made from the client's name, the town and the kind of home; nobody types it.
        </p>
      )}
    </div>
  );
}

/** A family member's home being recorded (090), as the form holds it before it is sent. */
export interface HouseholdDraft {
  residence_type: string;
  street_address: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  neighbourhood: string;
  notes: string;
}

export const EMPTY_HOUSEHOLD: HouseholdDraft = {
  residence_type: '', street_address: '', unit: '', city: '', state: 'New York', zip: '', county: '',
  neighbourhood: '', notes: '',
};

/** The draft as the API takes it, or null while something required is missing. */
export function householdInput(d: HouseholdDraft): NewHouseholdInput | null {
  if (!d.residence_type || !d.street_address.trim() || !d.city.trim() || d.state.trim().length < 2 ||
      d.zip.trim().length < 3 || (d.residence_type === 'Other' && !d.notes.trim())) {
    return null;
  }
  return {
    residence_type: d.residence_type,
    street_address: d.street_address.trim(),
    city: d.city.trim(),
    state: d.state.trim(),
    zip: d.zip.trim(),
    ...(d.unit.trim() ? { unit: d.unit.trim() } : {}),
    ...(d.county.trim() ? { county: d.county.trim() } : {}),
    ...(d.neighbourhood.trim() ? { neighbourhood: d.neighbourhood.trim() } : {}),
    ...(d.notes.trim() ? { notes: d.notes.trim() } : {}),
  };
}

/**
 * A family member's home the client lives at (090): a son's apartment, a
 * grandmother's house, a sister's house in another state.
 *
 * **Not the client's property.** There is no tenure question: the client does
 * not own or rent it, it never appears on Real Estate, and it raises no
 * property obligation. It is where a case manager goes.
 *
 * **No name, and no derived one.** It is shown by its street everywhere, so a
 * shared screen never says whose home it is. Who the family member is waits for
 * contacts; until then the notes may say it in words.
 */
export function HouseholdFields({
  draft, onChange,
}: {
  draft: HouseholdDraft;
  /** A state setter, so each field updates the latest draft (see HomeFields). */
  onChange: Dispatch<SetStateAction<HouseholdDraft>>;
}) {
  const types = useResidenceTypes('household');
  const set = <K extends keyof HouseholdDraft>(k: K) => (v: HouseholdDraft[K]) => onChange((d) => ({ ...d, [k]: v }));

  return (
    <div className="space-y-3 rounded-lg border border-neutral-300 p-3">
      <p className="text-sm text-neutral-700">
        A family member's home is where they live, not something they own or rent: it is not recorded
        as their property and never appears on Real Estate.
      </p>
      <fieldset>
        <legend className={label}>What kind of home</legend>
        <p className={hint}>This sets how often they must be visited while they live here.</p>
        <div className="mt-2 grid gap-1 sm:grid-cols-2">
          {(types.data?.residence_types ?? []).map((t) => (
            <label
              key={t.code}
              className={`flex min-h-[44px] cursor-pointer gap-3 rounded-lg border px-3 py-2 ${
                draft.residence_type === t.code ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200'
              }`}
            >
              <input type="radio" name="household-type" value={t.code} checked={draft.residence_type === t.code}
                     onChange={() => set('residence_type')(t.code)} className="mt-1 size-5 shrink-0" />
              <span>
                <span className="font-medium text-neutral-900">{t.label}</span>
                {t.description && <span className="block text-sm text-neutral-700">{t.description}</span>}
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <AddressFields
        prefix="household"
        street={draft.street_address} setStreet={set('street_address')}
        unit={draft.unit} setUnit={set('unit')}
        city={draft.city} setCity={set('city')}
        state={draft.state} setState={set('state')}
        zip={draft.zip} setZip={set('zip')}
        county={draft.county} setCounty={set('county')}
      />

      <NeighbourhoodField id="household-neighbourhood" value={draft.neighbourhood} onChange={set('neighbourhood')} />

      <div>
        <label className={label} htmlFor="household-notes">
          Whose home is it?{' '}
          <span className="font-normal text-neutral-500">
            {draft.residence_type === 'Other' ? 'and what kind of home' : '— in words, optional'}
          </span>
        </label>
        <textarea id="household-notes" className={`${field} min-h-[88px] py-2`} value={draft.notes} maxLength={4000}
                  placeholder="Her son's apartment"
                  onChange={(e) => set('notes')(e.target.value)} />
      </div>
    </div>
  );
}

function Failure({ error }: { error: Error | null }) {
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  return (
    <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-900">
      {error.message}
      {api?.hint && <span className="block text-rose-700">{api.hint}</span>}
    </p>
  );
}
